// Lib
import React, { ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { EditorContent } from '@tiptap/react';

// Utils
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { getMainEditorId, getMainEditorKey } from '../../utils/elementEditorUtils';

// Hooks
import useTaskTiptapEditorExtensions, { TaskTiptapEditorPasteHandlerArgs } from './useTaskTiptapEditorExtensions';
import useTiptapInnerRef from '../../../../common/tiptap/hooks/useTiptapInnerRef';
import usePoiUpdateHandler from '../../../components/pointsOfInterest/usePoiUpdateHandler';
import useClientElementTiptapEditor from '../../../components/tiptapEditor/useClientElementTiptapEditor';

// Components
import { SearchHighlightObserver } from '../../../../common/tiptap/extensions/SearchHighlighter';

// Types
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { TaskTiptapEditorKeyboardShortcutHandlerArgs } from './useTaskTiptapEditorKeyboardShortcutsExtension';
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import { SaveTiptapEditorContentFunction } from '../../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';

// Styles
import './TaskTiptapEditor.scss';

export type TaskTiptapEditorProps = TaskTiptapEditorKeyboardShortcutHandlerArgs &
    TaskTiptapEditorPasteHandlerArgs & {
        textContent: TiptapContent | null;

        element: ImMNElement;
        isEditable: boolean;
        isEditing: boolean;
        isFocusedForegroundElement: boolean;
        isParentTaskListSingleSelected: boolean;
        currentEditorKey: string;
        currentEditorId: string;
        spellCheck: boolean;

        placeholder: string;

        saveContent: SaveTiptapEditorContentFunction;
        startEditing: (args: { editorId: string; editorKey: string }) => void;
    };

const TaskTiptapEditor = (props: TaskTiptapEditorProps): ReactElement => {
    const {
        element,
        textContent,
        saveContent,
        isEditable,
        isEditing,
        isParentTaskListSingleSelected,
        currentEditorId,
        startEditing,
        placeholder,
        // Key handlers
        onEnter,
        onKeyboardDelete,
        changeIndentation,
        toggleComplete,
        dispatchOpenDueDatePopup,
        dispatchOpenAssignmentPopup,
        goToPreviousTask,
        goToNextTask,
        onStartBackspace,
        onEndDelete,
        // Paste handler
        createMultipleNewTasksWithContent,
    } = props;

    const dispatch = useDispatch();
    const editorId = getMainEditorId(props);
    const editorKey = getMainEditorKey(props);
    const elementId = getElementId(element);

    const { innerRef, contentInnerRef } = useTiptapInnerRef();
    const onHighlightsChanged = usePoiUpdateHandler({ contentInnerRef, elementId });

    const extensions = useTaskTiptapEditorExtensions({
        dispatch,
        placeholder,
        onHighlightsChanged,
        saveContent,
        isEditable,
        onEnter,
        onKeyboardDelete,
        changeIndentation,
        toggleComplete,
        dispatchOpenDueDatePopup,
        dispatchOpenAssignmentPopup,
        goToPreviousTask,
        goToNextTask,
        onStartBackspace,
        onEndDelete,
        createMultipleNewTasksWithContent,
    });

    const { editor, onMouseDown, onClick } = useClientElementTiptapEditor({
        extensions,
        persistedContent: textContent,

        editorId,
        editorKey,
        currentEditorId,

        isEditable,
        isEditing,
        // Always treat this editor as single-selected when the parent task list is single-selected
        isSingleSelected: isParentTaskListSingleSelected,

        startEditing,
        saveContent,

        // WEB-13628 - (temp fix) Prevent scrolls when de-indenting tasks
        _preventScroll: true,
    });

    return (
        <div className="TaskTiptapEditorContainer" onMouseDown={onMouseDown} onClick={onClick}>
            <SearchHighlightObserver editor={editor} />
            <EditorContent innerRef={innerRef} className="TaskTiptapEditor" dir="auto" editor={editor} />
        </div>
    );
};

export default TaskTiptapEditor;
